import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, FileText, ChatText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, scoreColor } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const TABS = [
  { id: "leads", label: "Leads", icon: ChatText },
  { id: "reports", label: "Raporlar", icon: FileText },
  { id: "users", label: "Kullanıcılar", icon: Users },
];

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [denied, setDenied] = useState(false);

  const load = async () => {
    try {
      const [l, r, u] = await Promise.all([
        api.get("/admin/leads"), api.get("/admin/reports"), api.get("/admin/users"),
      ]);
      setLeads(l.data); setReports(r.data); setUsers(u.data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleContacted = async (lead) => {
    try {
      await api.patch(`/admin/leads/${lead.id}`, { contacted: !lead.contacted });
      setLeads((p) => p.map((x) => (x.id === lead.id ? { ...x, contacted: !x.contacted } : x)));
    } catch { toast.error("Güncellenemedi"); }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  if (denied) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-heading text-2xl font-semibold">Erişim yetkiniz yok</h1>
          <p className="text-muted-foreground mt-2">Bu alan yalnızca yöneticilere açıktır.</p>
          <button onClick={() => navigate("/dashboard")} className="mt-4 text-brand font-medium">← Panele dön</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Yönetim</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-8">Admin Panel</h1>

        <div className="flex gap-1 border-b border-border mb-6">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-brand text-brand" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <t.icon size={16} /> {t.label}
              <span className="font-mono text-xs">({t.id === "leads" ? leads.length : t.id === "reports" ? reports.length : users.length})</span>
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div className="border border-border overflow-x-auto" data-testid="admin-leads">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left font-mono text-xs uppercase text-muted-foreground">
                <th className="p-3">Ad</th><th className="p-3">Şirket</th><th className="p-3">Sektör</th><th className="p-3">Telefon</th><th className="p-3">Mesaj</th><th className="p-3">Tarih</th><th className="p-3">Durum</th>
              </tr></thead>
              <tbody>
                {leads.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Henüz kayıt yok.</td></tr> :
                  leads.map((l) => (
                    <tr key={l.id} className="border-b border-border last:border-0">
                      <td className="p-3 font-medium">{l.name}</td>
                      <td className="p-3">{l.company || "—"}</td>
                      <td className="p-3">{l.sector || "—"}</td>
                      <td className="p-3 font-mono">{l.phone}</td>
                      <td className="p-3 max-w-xs truncate">{l.message || "—"}</td>
                      <td className="p-3 font-mono text-xs">{fmt(l.created_at)}</td>
                      <td className="p-3">
                        <button onClick={() => toggleContacted(l)} data-testid={`lead-toggle-${l.id}`}
                          className={`font-mono text-xs px-2 py-1 ${l.contacted ? "bg-score-goodbg text-score-good" : "bg-muted text-muted-foreground"}`}>
                          {l.contacted ? "Arandı ✓" : "Bekliyor"}
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "reports" && (
          <div className="border border-border overflow-x-auto" data-testid="admin-reports">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left font-mono text-xs uppercase text-muted-foreground">
                <th className="p-3">Marka</th><th className="p-3">Sektör</th><th className="p-3">Skor</th><th className="p-3">Durum</th><th className="p-3">Tarih</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {reports.map((r) => {
                  const c = scoreColor(r.overall_score);
                  return (
                    <tr key={r.id} className="border-b border-border last:border-0">
                      <td className="p-3 font-medium">{r.brand_name}</td>
                      <td className="p-3">{r.sector || "—"}</td>
                      <td className="p-3">{r.status === "done" ? <span className="font-mono font-bold" style={{ color: c.hex }}>{r.overall_score}</span> : "—"}</td>
                      <td className="p-3"><span className={`font-mono text-xs ${r.status === "failed" ? "text-score-poor" : r.status === "done" ? "text-score-good" : "text-score-medium"}`}>{r.status}</span></td>
                      <td className="p-3 font-mono text-xs">{fmt(r.created_at)}</td>
                      <td className="p-3"><button onClick={() => navigate(`/r/${r.id}`)} className="text-brand text-xs font-medium">Görüntüle →</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "users" && (
          <div className="border border-border overflow-x-auto" data-testid="admin-users">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left font-mono text-xs uppercase text-muted-foreground">
                <th className="p-3">Ad</th><th className="p-3">E-posta</th><th className="p-3">Plan</th><th className="p-3">Rapor</th><th className="p-3">Kayıt</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-border last:border-0">
                    <td className="p-3 font-medium">{u.name || "—"}</td>
                    <td className="p-3 font-mono text-xs">{u.email}</td>
                    <td className="p-3">{u.plan}</td>
                    <td className="p-3 tabular-nums">{u.report_count}</td>
                    <td className="p-3 font-mono text-xs">{fmt(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
